import { Component, EventEmitter, Input, OnInit, ViewEncapsulation } from '@angular/core'
import { UntypedFormControl, Validators } from '@angular/forms'
import { Charity, CharityService, MessageService } from "@bequeathed/ngx-services"
import { LoggerService } from "@bequeathed/ngx-app-core"
import { PageUtil } from "@bequeathed/ngx-utils"

const LOG: LoggerService = LoggerService.get('CharityComponent')
declare let paypal: any

/**
 * Component to handle interaction with the PayPal Giving app
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: 'app-charity',
    templateUrl: './charity.component.html',
    styleUrls: ['./charity.component.sass'],
    encapsulation: ViewEncapsulation.None
})
export class CharityComponent implements OnInit {

    // @ts-expect-error injected
    @Input() userId: number
    // @ts-expect-error injected
    @Input() interviewId: number
    @Input() modal: any
    // @ts-expect-error injected
    @Input() charity: Charity
    // @ts-expect-error injected
    @Input() paymentErrorTrigger: EventEmitter<any>
    // @ts-expect-error injected
    @Input() paymentMadeTrigger: EventEmitter<any>
    // @ts-expect-error injected
    @Input() paymentCancelledTrigger: EventEmitter<any>

    // @ts-expect-error this is fine
    donationAmount: UntypedFormControl
    // @ts-expect-error this is fine
    consentToShareDetails: UntypedFormControl
    // @ts-expect-error this is fine
    includeGiftAid: UntypedFormControl
    loading = true
    errored = false

    paymentStatus: string | undefined
    paymentError: string | undefined

    private orderId: string | undefined

    constructor(
        private charityService: CharityService,
        private messageService: MessageService) {
    }

    get messages(): MessageService {
        return this.messageService
    }

    ngOnInit(): void {
        if (!this.charity) {
            LOG.warn('Charity is null!!')
            return
        }

        LOG.debug('init', `Loading charity: ${this.charity.initiator}`)

        this.donationAmount = new UntypedFormControl('', Validators.required)
        this.consentToShareDetails = new UntypedFormControl(false, Validators.requiredTrue)
        this.includeGiftAid = new UntypedFormControl()
        this.loading = false
        this.errored = false
        this.orderId = undefined
        this.paymentStatus = undefined
        this.paymentError = undefined

        PageUtil.setFocus('modal-basic-title')
        setTimeout(() => this.donationAmount!.markAsUntouched(), 55)

        // Delay added in attempt to handle possible race condition with the PayPal SDK
        setTimeout(
            () =>
                paypal.Buttons({
                    style: {
                        label: 'donate',
                        layout: 'horizontal',
                        color: 'blue',
                        shape: 'pill',
                        tagline: false
                    },

                    onInit: (data: any, actions: any) => {
                        actions.disable()

                        const values = [ this.donationAmount!.valueChanges, this.consentToShareDetails!.valueChanges ]
                        values.forEach((control: any) =>
                                control.subscribe(() => {
                                    if (this.validate()) {
                                        actions.enable()
                                    } else {
                                        actions.disable()
                                    }
                                })
                            )

                    },
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    onClick: (data: any, actions: any) => {
                        this.errored = false
                        if (!this.validate()) {
                            if (this.donationAmount!.invalid) {
                                PageUtil.setFocus('charity-amount')
                            } else if (this.consentToShareDetails!.invalid) {
                                PageUtil.setFocus('consent-to-share')
                            }
                        }

                    },
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    createOrder: async (data: any, actions: any) => {
                        if (this.errored) {
                            return
                        }

                        LOG.debug('init', `Creating order for: ${this.charity.name}`)
                        this.loading = true
                        try {
                            this.orderId = await this.charityService.createPayPalOrder(
                                this.charity.nonProfitId!,
                                this.charity.name!,
                                this.donationAmount!.value,
                                this.includeGiftAid!.value,
                                this.consentToShareDetails!.value,
                                this.userId,
                                this.interviewId
                            )
                        } catch (e) {
                            LOG.warn(`Error creating order! ${e}`)
                        }
                        if (!this.orderId) {
                            this.paymentStatus = 'FAILED'
                            PageUtil.setFocus('payment-made-text')
                            return
                        }
                        return this.orderId
                    },
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    onApprove: async (data: any, actions: any) => {
                        LOG.debug('init', `Capturing order for: ${this.charity.name}`)
                        const donation: any = await this.charityService.capturePayPalOrder(this.orderId!)

                        this.paymentStatus = donation.paymentStatus

                        if (this.paymentStatus === 'CONFIRMED') {
                            setTimeout(
                                () => {
                                    this.paymentMadeTrigger.next(undefined)
                                    this.modal.close()
                                },
                                5000
                            )
                        }

                        PageUtil.setFocus('payment-made-text')
                    },
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    onCancel: async (data: any) => {
                        LOG.debug('init', `Cancelling order for: ${this.charity.name}`)
                        await this.charityService.cancelPayPalOrder(this.orderId!)
                        this.loading = false

                        this.paymentStatus = 'CANCELLED'

                        PageUtil.setFocus('payment-made-text')
                    }
                })
                .render(`#charity-button-${this.charity.nonProfitId}`),
            250
        )
    }

    private validate(): boolean {
        LOG.debug('validate', 'Validating form...')
        const hasCurrentError: boolean = (this.donationAmount!.invalid || this.consentToShareDetails!.invalid)
        this.errored = this.errored || hasCurrentError
        return !hasCurrentError
    }

    confirmCancellation(): void {
        this.paymentCancelledTrigger.next(undefined)
        this.modal.close()
    }
}
